/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, Button, Form, Badge,
} from 'react-bootstrap';
import { publicVehicleService } from '../publicFleetServices';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import setStateFromInput from '../../../Utils/setStateFromInput';

/**
 * The operator's vehicles: list, add, edit, deactivate.
 *
 * `service` is `{ list(), save(record), remove(id) }` — see
 * DriverManagementSection. Omitted, it is the public page's link-based
 * calls scoped by `operatorId`.
 */
function FleetManagementSection({ operatorId, setError, service }) {
  const api = useMemo(
    () => service || publicVehicleService(operatorId),
    [service, operatorId],
  );
  const enabled = Boolean(service || operatorId);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCarId, setEditingCarId] = useState(null);
  const [formData, setFormData] = useState({
    type: '',
    color: '',
    plate_number: '',
    active: 1,
  });

  const fetchCars = async () => {
    if (!enabled) return;

    setLoading(true);
    try {
      setCars(await api.list());
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (enabled) {
      fetchCars();
    }
  }, [api]);

  const handleChange = (e) => {
    setStateFromInput(e, setFormData);
  };

  const resetForm = () => {
    setFormData({
      type: '',
      color: '',
      plate_number: '',
      active: 1,
    });
    setValidationErrors([]);
  };

  const handleAddCar = async () => {
    setLoading(true);
    try {
      await api.save({
        ...formData,
        active: Number(formData.active),
      });
      await fetchCars();
      setShowAddForm(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add vehicle');
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCar = async (car) => {
    setLoading(true);
    try {
      await api.save({
        Id: car.Id,
        type: car.type,
        color: car.color,
        plate_number: car.plate_number,
        active: Number(car.active),
      });
      await fetchCars();
      setEditingCarId(null);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update vehicle');
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCar = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to deactivate this vehicle?')) {
      return;
    }

    setLoading(true);
    try {
      await api.remove(id);
      await fetchCars();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete vehicle');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (car) => {
    setEditingCarId(car.Id);
  };

  const cancelEditing = () => {
    setEditingCarId(null);
    fetchCars();
  };

  const handleEditChange = (carId, field, value) => {
    setCars((prevCars) => prevCars.map((c) => (
      c.Id === carId ? { ...c, [field]: value } : c
    )));
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="h5 mb-0">Your Vehicles</h2>
        {!showAddForm && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowAddForm(true)}
            disabled={loading}
          >
            <i className="fa fa-plus me-2" />
            Add New Vehicle
          </Button>
        )}
      </div>

      {showAddForm && (
        <div className="card mb-3 bg-light">
          <div className="card-body">
            <h6 className="card-title">Add New Vehicle</h6>
            <Form>
              <div className="row">
                <Form.Group className="mb-2 col-md-4" controlId="type">
                  <Form.Label>Brand & Model *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.type}
                    onChange={handleChange}
                    size="sm"
                    placeholder="e.g., Skoda Octavia, BMW X5"
                  />
                  <FormControlFeedback errors={validationErrors} field="type" />
                </Form.Group>

                <Form.Group className="mb-2 col-md-4" controlId="color">
                  <Form.Label>Color</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.color}
                    onChange={handleChange}
                    size="sm"
                    placeholder="e.g., Black, White"
                  />
                  <FormControlFeedback errors={validationErrors} field="color" />
                </Form.Group>

                <Form.Group className="mb-2 col-md-4" controlId="plate_number">
                  <Form.Label>Plate Number *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.plate_number}
                    onChange={handleChange}
                    size="sm"
                    placeholder="e.g., ABC-123"
                  />
                  <FormControlFeedback errors={validationErrors} field="plate_number" />
                </Form.Group>
              </div>

              <div className="row">
                <Form.Group className="mb-2 col-md-4" controlId="active">
                  <Form.Label>Active *</Form.Label>
                  <Form.Select
                    value={formData.active}
                    onChange={handleChange}
                    size="sm"
                  >
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="d-flex gap-2">
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleAddCar}
                  disabled={loading}
                >
                  <i className="fa fa-check me-2" />
                  Save
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setShowAddForm(false);
                    resetForm();
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}

      <Table striped bordered hover size="sm" responsive className="fleet-driver-table">
        <thead>
          <tr>
            <th>Brand & Model</th>
            <th>Color</th>
            <th>Plate Number</th>
            <th>Active</th>
            <th style={{ width: '150px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {cars.length === 0 && (
            <tr>
              <td colSpan="5" className="text-center text-muted">
                No vehicles found. Add your first vehicle above.
              </td>
            </tr>
          )}
          {cars.map((car) => (
            <tr
              key={car.Id}
              className={car.active === 0 ? 'text-muted' : ''}
            >
              {editingCarId === car.Id ? (
                <>
                  <td data-label="Type">
                    <Form.Control
                      type="text"
                      value={car.type}
                      onChange={(e) => handleEditChange(car.Id, 'type', e.target.value)}
                      size="sm"
                    />
                  </td>
                  <td data-label="Color">
                    <Form.Control
                      type="text"
                      value={car.color || ''}
                      onChange={(e) => handleEditChange(car.Id, 'color', e.target.value)}
                      size="sm"
                    />
                  </td>
                  <td data-label="Plate">
                    <Form.Control
                      type="text"
                      value={car.plate_number}
                      onChange={(e) => handleEditChange(car.Id, 'plate_number', e.target.value)}
                      size="sm"
                    />
                  </td>
                  <td data-label="Active">
                    <Form.Select
                      value={car.active}
                      onChange={(e) => handleEditChange(car.Id, 'active', Number(e.target.value))}
                      size="sm"
                    >
                      <option value="1">Yes</option>
                      <option value="0">No</option>
                    </Form.Select>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="success"
                        size="sm"
                        aria-label="Save vehicle"
                        onClick={() => handleUpdateCar(car)}
                        disabled={loading}
                      >
                        <i className="fa fa-check" aria-hidden="true" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label="Cancel editing"
                        onClick={cancelEditing}
                        disabled={loading}
                      >
                        <i className="fa fa-times" aria-hidden="true" />
                      </Button>
                    </div>
                  </td>
                </>
              ) : (
                <>
                  <td data-label="Type">{car.type}</td>
                  <td data-label="Color">{car.color || '-'}</td>
                  <td data-label="Plate">{car.plate_number}</td>
                  <td data-label="Active">
                    {car.active === 1 ? (
                      <Badge bg="success">Active</Badge>
                    ) : (
                      <Badge bg="danger">Inactive</Badge>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="primary"
                        size="sm"
                        aria-label={`Edit vehicle ${car.plate_number || car.type || ''}`.trim()}
                        onClick={() => startEditing(car)}
                        disabled={loading}
                      >
                        <i className="fa fa-edit" aria-hidden="true" />
                      </Button>
                      {car.active === 1 && (
                        <Button
                          variant="danger"
                          size="sm"
                          aria-label={`Deactivate vehicle ${car.plate_number || car.type || ''}`.trim()}
                          onClick={() => handleDeleteCar(car.Id)}
                          disabled={loading}
                        >
                          <i className="fa fa-trash" aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

export default FleetManagementSection;
